import type Dexie from 'dexie';

/**
 * **Product migration v001 — the first shipped schema.**
 *
 * NFR-7: *"Numbered forward-only Dexie migrations from v001; a fixture DB from the first shipped
 * schema must survive every later migration, and this is tested."* This is that v001. The fixture is
 * `src/data/fixtures/v001Fixture.ts` and `fixtureMigration.test.ts` drives it through the whole
 * chain on every test run.
 *
 * **This file is never edited once it has run on a device.** A device that already applied v001 will
 * never apply it again, so an edit produces two different v001 schemas in the field — one on new
 * installs, one on existing phones — with nothing to tell them apart. Schema changes are new
 * numbered files appended to `./index.ts`.
 *
 * Design §13.3: **migrations never import `domain/`** — plain rows only — so a later refactor of
 * domain semantics cannot retroactively change what an old migration does. The only import here is
 * the Dexie *type*.
 *
 * ── Index choices (design §3.4) ──────────────────────────────────────────────
 *
 * `observations` and `hypotheses` carry **exactly** the index set build unit U-0 measured against.
 * Index maintenance is a material part of an IndexedDB commit's cost and both tables sit inside the
 * design §5.1 tap transaction, so an index added here that U-0 did not measure invalidates A-1's
 * number. Adding one is a decision to re-run U-0, not a free lookup.
 *
 *   observations  [matchId+hypothesisId+kind+createdAt]  LIVE-6 undo target selection. The range
 *                                                        **structurally cannot** contain another
 *                                                        hypothesis's rows or a voiceNote/flag —
 *                                                        that is INV-1's mechanism, not a filter.
 *                 [matchId+createdAt]                    replay, sync staleness, SES-8 re-derivation
 *                 matchId, hypothesisId                  cascade (INV-8) and per-hypothesis reads
 *   hypotheses    &[matchId+boardPosition]               INV-5. Compound index entries with a `null`
 *                                                        component are OMITTED from an IndexedDB
 *                                                        index, so list-only rows are correctly
 *                                                        unconstrained. Do not "fix" this.
 *                 [matchId+statusOverride]               board render (§6.3)
 *                 [matchId+cachedCount]                  LOCK-2, DIST-2
 *   talkPoints    &[matchId+phase+rank]                  INV-6
 *   flagResolutions &observationId                       DIST-1 — one resolution per flag, which is
 *                                                        what makes "a resolved flag MUST NOT
 *                                                        reappear in the queue" enforceable
 *   players       &[teamId+number]                       PRE-4 upsert. Without it the upsert silently
 *                                                        degrades to an insert and FILE-2's
 *                                                        cross-match player linkage duplicates a
 *                                                        shirt number per match.
 *   outbox        [kind+scopeId] as PRIMARY KEY          SYN-10 coalescing — see the note on
 *                                                        `OutboxRow` in `types/`
 *
 * ── Two things that are deliberately NOT indexed ─────────────────────────────
 *
 * 1. **No boolean is ever an index.** IndexedDB does not accept a boolean as a key, so an index on
 *    `audioBlobs.complete`, `matches.abandoned`, `setPieces.draft` or `templates.builtin` would be
 *    declared, accepted, and hold **no entries at all** — a query against it silently returns
 *    nothing. Build unit U-1 found this on `audioSegments.complete`. These are filtered in memory
 *    against a `matchId`-bounded set instead.
 * 2. **No `audioBlobId` index on `observations`.** The cascade reaches blobs through the
 *    observations it has already loaded by `matchId`, so the index would buy nothing and cost a
 *    write on the hottest table in the app.
 */
export function v001Initial(db: Dexie): void {
  db.version(1).stores({
    // ── Profile scope (SYN-4's "profile archive") ────────────────────────────
    teams: 'id, name, createdAt',
    players: 'id, &[teamId+number], teamId',
    templates: 'id, name',
    settings: 'key',

    // ── Match scope ──────────────────────────────────────────────────────────
    matches: 'id, teamId, [teamId+date], state, date',
    hypotheses:
      'id, &[matchId+boardPosition], [matchId+statusOverride], [matchId+cachedCount], matchId',
    observations:
      'id, [matchId+hypothesisId+kind+createdAt], [matchId+createdAt], matchId, hypothesisId',
    flagResolutions: 'id, &observationId, promotedHypothesisId',
    setPieces: 'id, matchId, [matchId+matchMinute]',
    talkPoints: 'id, &[matchId+phase+rank], matchId, sourceHypothesisId, attachedSetPieceId',
    matchNotes: 'id, matchId, [matchId+phase]',

    // ── Audio (DD-2 §8.3) — its own object store, in the same IndexedDB, so INV-8's cascade is
    //    atomic across rows *and* blobs, and multi-megabyte writes stay out of the tap transaction.
    audioBlobs: 'id, createdAt',

    // ── Device-local bookkeeping. All four are on VER-7's justified exclusion list (§17.4). ──
    outbox: '[kind+scopeId], state, enqueuedAt',
    uiSession: 'id',
    activeMatch: 'id',
    logEntries: '++seq, ts, level, module, matchId',
  });
}
